// "use client"
import { gql } from '@apollo/client';

import React, { useEffect, useState } from 'react';

import ChevronLeftSmall from '@/assets/icons/chevron-left-small.svg';
import ChevronRightSmall from '@/assets/icons/chevron-right-small.svg';
import Dot from '@/assets/icons/dot.svg';
import IconPlaceholder from '@/assets/icons/icon-placeholder.svg';
import { useSwipe } from '@/hooks/use-swipe';

import { MeetingPlaceLinkToAnyPageFragment } from '@/lib/fragments/meetingPlaceLinkToAnyPage';

import LinkWrapper from '@/components/link-wrapper/link-wrapper';
import Image from 'next/image';
import {
  useTenantConfig,
  useUrlSubDirectoryDictionary,
} from '@/providers/ConfigProvider';
import ImageWrapper from '@/components/image-wrapper/Image-wrapper';
import { CrowdIndicator } from '@/components/header/crowd-indicator';
import { useCrowdLevel } from '@/context/CrowdLevelContext';
import ExperienceSlot from '../experience-slot/experience-slot';
import MainBannerSlideContent from './main-banner-slide-content';

interface MainBannerListItemBase {
  id: string;
  sys?: { id: string };
  text: string;
  title: string;
  overlay: boolean;
  mediaField: {
    jsonBynderAsset: {
      originalUrl: string;
      thumbnail: string;
    };
    altText: string;
  };
  image: any;
  readMoreButtonTitle: string;
  link?: any;
  externalLink?: string;
}

// A Ninetailed experience attached to a banner (item-level EntryReplacement).
// Its variants are BlockMainBanner entries carrying the same presentational
// fields as the baseline banner, so they render identically once selected.
// Shape mirrors the `MainBannerExperiences` fragment selection exactly: the
// experience entry exposes `ntExperienceId` (the Experience API id — NOT a
// Contentful `sys.id`), and `ntAudience` carries `ntAudienceId`/`ntName` only.
export interface NinetailedBannerExperience {
  ntExperienceId?: string;
  ntName?: string;
  ntType?: string;
  ntConfig?: Record<string, unknown>;
  ntAudience?: {
    ntAudienceId?: string;
    ntName?: string;
  } | null;
  ntVariantsCollection?: {
    items: Array<MainBannerListItemBase>;
  };
}

interface MainBannerListItem extends MainBannerListItemBase {
  ntExperiencesCollection?: {
    items: Array<NinetailedBannerExperience>;
  };
}

export interface MainBannerListBlockProps {
  mainBannerItemsCollection: {
    items: Array<MainBannerListItem>;
  };
  meetingPlaceName?: string;
  logo?: any;
  node_locale: string;
}

const MainBannerListBlock = ({
  mainBannerItemsCollection,
  logo,
  meetingPlaceName,
  node_locale,
}: MainBannerListBlockProps) => {
  const mainBannerItems = mainBannerItemsCollection?.items?.filter(Boolean);
  const [currentBanner, setCurrentBanner] = useState<number>(0);
  const crowdLevel = useCrowdLevel();
  const numberOfBanners = mainBannerItems?.length - 1;
  const timeoutRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const config = useTenantConfig();
  const prefix = config?.GATSBY_SITE_PATH_PREFIX || '';
  const showCrowdIndicator =
    config?.NEXT_PUBLIC_SHOW_CROWD_INDICATOR === 'true';

  const urlSubDirectorydictionary = useUrlSubDirectoryDictionary();

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  // autoplay
  useEffect(() => {
    resetTimeout();
    timeoutRef.current = setInterval(nextBanner, 5000);
    () => resetTimeout();
  }, [currentBanner]);

  const nextBanner = (): void => {
    if (currentBanner < numberOfBanners) {
      setCurrentBanner((prevBanner) => prevBanner + 1);
    } else if (currentBanner === numberOfBanners) {
      setCurrentBanner(0);
    }
  };

  const previousBanner = (): void => {
    if (currentBanner > 0) {
      setCurrentBanner((prevBanner) => prevBanner - 1);
    } else if (currentBanner === 0) {
      setCurrentBanner(numberOfBanners);
    }
  };

  const swipeHandlers = useSwipe({
    onSwipeLeft: nextBanner,
    onSwipeRight: previousBanner,
  });

  const SliderNavigation = () => {
    return (
      <ul className="absolute bottom-16 left-0 right-0 z-10 mb-28 md:mb-12 sm:mb-10 flex w-full justify-center lg:mx-7 lg:mb-0 lg:justify-start lg:pl-4">
        <li>
          <button
            type="button"
            title="Previous"
            onClick={previousBanner}
            className={
              'mr-6 hidden h-8 w-8 rounded-full bg-brand-white lg:inline'
            }
          >
            <ChevronLeftSmall className="inline-block" />
          </button>
        </li>
        {mainBannerItems?.map((_, index) => {
          return (
            <li
              key={`slider-navigation-indicator-${index}`}
              className="mx-1.5 flex items-center"
              onClick={() => setCurrentBanner(index)}
            >
              {index === currentBanner ? (
                <IconPlaceholder className="brightness-0 invert" />
              ) : (
                <Dot className="brightness-0 invert" />
              )}
            </li>
          );
        })}
        <li>
          <button
            title="Next"
            type="button"
            onClick={nextBanner}
            className={
              'ml-6 hidden h-8 w-8 rounded-full bg-brand-white lg:inline'
            }
          >
            <ChevronRightSmall className="inline-block" />
          </button>
        </li>
      </ul>
    );
  };

  const hiddenSlide = 'opacity-0 pointer-events-none';
  const displayedSlide = 'opacity-100 pointer-events-auto';
  const aspectRatio =
    logo?.width && logo?.height ? logo.width / logo.height : undefined;

  return (
    <section className="relative top-0 z-10 bg-brand-white lg:sticky lg:mx-0 lg:mt-0 lg:pt-6">
      {showCrowdIndicator && crowdLevel !== null && (
        <div
          className="absolute right-5 top-12 z-10 lg:hidden"
          style={{ marginTop: 2 }}
        >
          <CrowdIndicator
            level={crowdLevel}
            maxLevel={4}
            variant="homepage-mobile"
          />
        </div>
      )}
      {logo && (
        <LinkWrapper
          data={`/${node_locale}`}
          className="absolute left-8 top-12 lg:top-12 sm:top-6 z-10 block lg:left-12"
        >
          <ImageWrapper
            alt="Logotype"
            url={logo.url}
            width={144}
            height={60}
            imgClassName={
              aspectRatio == 1 ? 'max-h-15 !w-auto' : 'max-h-10 !w-auto'
            }
            className={aspectRatio == 1 ? 'max-h-15 w-36' : 'max-h-10 w-36'}
          />
        </LinkWrapper>
      )}

      <div className="relative flex h-screen overflow-hidden  lg:h-[calc(100vh-3rem)]">
        {mainBannerItems?.map((banner, index) => {
          return (
            <div
              onTouchStart={swipeHandlers.onTouchStart}
              onTouchMove={swipeHandlers.onTouchMove}
              onTouchEnd={swipeHandlers.onTouchEnd}
              key={`${banner.id}-${index}`}
              className={`group absolute inset-0 transition-all delay-200 duration-500 lg:rounded-xxl ${
                currentBanner === index ? displayedSlide : hiddenSlide
              }`}
              id={`banner-${index}`}
            >
              {(() => {
                const slideContent = {
                  id: banner.sys?.id ?? banner.id,
                  title: banner.title,
                  text: banner.text,
                  overlay: banner.overlay,
                  mediaField: banner.mediaField,
                  readMoreButtonTitle: banner.readMoreButtonTitle,
                  link: banner.link,
                  externalLink: banner.externalLink,
                };
                const passthroughProps = {
                  isActive: currentBanner === index,
                  node_locale,
                  prefix,
                  urlSubDirectorydictionary,
                };
                const hasExperiences =
                  !!banner.ntExperiencesCollection?.items?.length &&
                  !!banner.sys?.id;

                return hasExperiences ? (
                  <ExperienceSlot
                    component={MainBannerSlideContent}
                    baseline={slideContent}
                    experiences={banner.ntExperiencesCollection}
                    passthroughProps={passthroughProps}
                  />
                ) : (
                  <MainBannerSlideContent
                    {...slideContent}
                    {...passthroughProps}
                  />
                );
              })()}
            </div>
          );
        })}
        <SliderNavigation />
      </div>
    </section>
  );
};

export default MainBannerListBlock;

